/**
 * SourceFunnel.tsx — Pipeline source funnel.
 * Wired to live Supabase data.
 *
 * Route: /reporting/source-funnel
 *
 * Funnel stages (per source):
 *   leads      — hunt_items grouped by source (or "Unattributed" if null)
 *   engaged    — hunt_items where outcome IS NOT NULL or status != "open"
 *   qualified  — hunt_items linked to an account (account_id non-null is the table FK,
 *                so this is "linked to a known account")
 *   opp        — distinct accounts that appear in v_account_forecast
 *   won        — forecast bucket = WON
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  ForecastRow,
  HuntItemRow,
  fmtUSD,
  listAccountForecasts,
  listAccounts,
  listAllHunt,
  pctOf,
} from "@/lib/reporting-live";

interface FunnelRow {
  source: string;
  leads: number;
  engaged: number;
  qualified: number;
  opp: number;
  won: number;
  weightedValue: number;
}

function sourceOf(h: HuntItemRow): string {
  return (h.source && h.source.trim()) || "Unattributed";
}

export default function SourceFunnel() {
  const [hunt, setHunt] = useState<HuntItemRow[] | null>(null);
  const [forecasts, setForecasts] = useState<ForecastRow[]>([]);
  const [accountIds, setAccountIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [h, fc, acc] = await Promise.all([
          listAllHunt(),
          listAccountForecasts(),
          listAccounts(),
        ]);
        if (cancelled) return;
        setHunt(h);
        setForecasts(fc);
        setAccountIds(new Set(acc.map((a) => a.id)));
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows: FunnelRow[] = useMemo(() => {
    if (!hunt) return [];
    const accountToForecast = new Map<number, ForecastRow>();
    forecasts.forEach((f) => accountToForecast.set(f.account_id, f));

    const bySource = new Map<string, HuntItemRow[]>();
    for (const h of hunt) {
      const k = sourceOf(h);
      if (!bySource.has(k)) bySource.set(k, []);
      bySource.get(k)!.push(h);
    }

    const out: FunnelRow[] = [];
    bySource.forEach((items, source) => {
      const leads = items.length;
      const engaged = items.filter((i) => (i.outcome && i.outcome !== "") || i.status !== "open").length;
      const qualified = items.filter((i) => accountIds.has(i.account_id)).length;
      const accountsInSource = new Set(items.map((i) => i.account_id).filter((id) => accountIds.has(id)));
      let opp = 0;
      let won = 0;
      let weightedValue = 0;
      accountsInSource.forEach((id) => {
        const f = accountToForecast.get(id);
        if (f) {
          opp += 1;
          weightedValue += Number(f.weighted_value || 0);
          if ((f.forecast_bucket || "").toUpperCase() === "WON") won += 1;
        }
      });
      out.push({ source, leads, engaged, qualified, opp, won, weightedValue });
    });
    return out.sort((a, b) => b.leads - a.leads);
  }, [hunt, forecasts, accountIds]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          leads: acc.leads + r.leads,
          engaged: acc.engaged + r.engaged,
          qualified: acc.qualified + r.qualified,
          opp: acc.opp + r.opp,
          won: acc.won + r.won,
          weightedValue: acc.weightedValue + r.weightedValue,
        }),
        { leads: 0, engaged: 0, qualified: 0, opp: 0, won: 0, weightedValue: 0 },
      ),
    [rows],
  );

  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Source Funnel" }]}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <a
          href="/reporting"
          style={{ fontSize: 12, color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
        >
          ← Reporting Hub
        </a>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0B0B0F", margin: "10px 0 4px" }}>
          Source Funnel
        </h1>
        <div style={{ fontSize: 13, color: "#6B6B70", marginBottom: 20 }}>
          Conversion through the funnel by lead source. Pulled from hunt_items, joined to the
          live account forecast.
        </div>

        {error && (
          <div
            style={{
              padding: 12,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#991B1B",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* Totals strip */}
        <div
          style={{
            background: "#0B0B0F",
            borderRadius: 12,
            padding: "16px 24px",
            marginBottom: 20,
            display: "flex",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <Stat label="Leads" value={totals.leads} color="#FAFAF7" />
          <Stat label="Engaged" value={totals.engaged} color="#86EFAC" />
          <Stat label="Qualified" value={totals.qualified} color="#86EFAC" />
          <Stat label="Opp" value={totals.opp} color="#86EFAC" />
          <Stat label="Won" value={totals.won} color="#86EFAC" />
          <Stat label="Weighted $" value={fmtUSD(totals.weightedValue)} color="#FDE68A" />
        </div>

        {/* Table */}
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#FAFAF7" }}>
                {[
                  "Source",
                  "Leads",
                  "Engaged",
                  "Eng %",
                  "Qualified",
                  "Qual %",
                  "Opp",
                  "Won",
                  "Weighted $",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: "#8E8E94",
                      borderBottom: "1px solid #EFEFEE",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hunt === null ? (
                <tr>
                  <td colSpan={9} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 20, color: "#8E8E94", textAlign: "center" }}>
                    No hunt items recorded yet.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.source} style={{ borderTop: "1px solid #FAFAF7" }}>
                    <td style={{ padding: "10px 14px", color: "#0B0B0F", fontWeight: 600 }}>
                      {r.source}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#0B0B0F" }}>{r.leads}</td>
                    <td style={{ padding: "10px 14px", color: "#525258" }}>{r.engaged}</td>
                    <td style={{ padding: "10px 14px", color: "#6B6B70" }}>
                      {pctOf(r.engaged, r.leads)}%
                    </td>
                    <td style={{ padding: "10px 14px", color: "#525258" }}>{r.qualified}</td>
                    <td style={{ padding: "10px 14px", color: "#6B6B70" }}>
                      {pctOf(r.qualified, r.leads)}%
                    </td>
                    <td style={{ padding: "10px 14px", color: "#525258" }}>{r.opp}</td>
                    <td style={{ padding: "10px 14px", color: "#16A34A", fontWeight: 700 }}>
                      {r.won}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#0B0B0F" }}>
                      {fmtUSD(r.weightedValue)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#8E8E94" }}>
          Source = hunt_items.source. Engaged = outcome set or status moved off "open". Qualified
          = linked to a known account in this tenant. Opp = appears in v_account_forecast.
        </div>
      </div>
    </OperatorShell>
  );
}

function Stat({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#525258",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}
